import React, { useState } from 'react';
import QRCode from 'react-qr-code';
import { Printer, ArrowLeft, QrCode } from 'lucide-react';

const PHASE_LABELS = {
  germination: 'Keimung', seedling: 'Sämling', vegetative: 'Vegetativ',
  flowering: 'Blüte', harvest: 'Ernte',
};

function QRLabel({ plant }) {
  const qrValue = `${window.location.origin}/plants?highlight=${plant.id}`;
  return (
    <div
      className="qr-label"
      data-testid={`qr-label-${plant.id}`}
      style={{
        width: '85mm', minHeight: '54mm', padding: '4mm',
        background: '#fff', border: '1px solid #ccc', borderRadius: '4px',
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        gap: '3mm', pageBreakInside: 'avoid', fontFamily: 'sans-serif',
        boxSizing: 'border-box',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '4mm', width: '100%' }}>
        <QRCode value={qrValue} size={90} style={{ flexShrink: 0 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: '14px', fontWeight: 700, color: '#111', wordBreak: 'break-word' }}>
            {plant.name}
          </div>
          {plant.strain && (
            <div style={{ fontSize: '11px', color: '#555', marginTop: '2px' }}>{plant.strain}</div>
          )}
          <div style={{
            fontSize: '10px', color: '#10B981', fontWeight: 600, marginTop: '4px',
            background: '#f0fdf4', padding: '1px 6px', borderRadius: '99px', display: 'inline-block',
          }}>
            {PHASE_LABELS[plant.phase] || plant.phase}
          </div>
          {plant.start_date && (
            <div style={{ fontSize: '9px', color: '#999', marginTop: '4px' }}>
              Start: {new Date(plant.start_date).toLocaleDateString('de-DE')}
            </div>
          )}
          {plant.project_name && (
            <div style={{ fontSize: '9px', color: '#888', marginTop: '2px' }}>
              {plant.project_name}
            </div>
          )}
        </div>
      </div>
      <div style={{
        fontSize: '8px', color: '#bbb', borderTop: '1px solid #eee',
        width: '100%', paddingTop: '2mm', textAlign: 'center',
      }}>
        GrowManager · {plant.id.slice(-8).toUpperCase()}
      </div>
    </div>
  );
}

export default function QRLabelsPage({ plants = [], projects = [], filterProject: initialFilter = '', onClose }) {
  const [filterProject, setFilterProject] = useState(initialFilter);
  const [selected, setSelected] = useState(() => new Set(plants.map(p => p.id)));

  const filteredPlants = filterProject
    ? plants.filter(p => p.project_id === filterProject)
    : plants;

  // Enrich plants with project name for QR label display
  const enrichedPlants = filteredPlants.map(p => ({
    ...p,
    project_name: projects.find(pr => pr.id === p.project_id)?.name || '',
  }));

  // Keep selection in sync when filter changes
  const togglePlant = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const selectedPlants = enrichedPlants.filter(p => selected.has(p.id));

  const selectAll   = () => setSelected(new Set(enrichedPlants.map(p => p.id)));
  const selectNone  = () => setSelected(new Set());

  const handlePrint = () => window.print();

  return (
    <div data-testid="qr-labels-page">
      {/* Print styles */}
      <style>{`
        @media print {
          .no-print { display: none !important; }
          .print-grid {
            display: grid !important;
            grid-template-columns: repeat(2, 1fr) !important;
            gap: 4mm !important;
            padding: 8mm !important;
            background: white !important;
          }
          body { background: white !important; }
          .qr-label { border-color: #999 !important; }
        }
      `}</style>

      {/* Controls – hidden on print */}
      <div className="no-print p-4 md:p-6 space-y-4 animate-fade-in">
        {/* Header */}
        <div className="flex items-center gap-3">
          <button
            onClick={onClose}
            className="p-2 rounded-lg text-emerald-100/60 hover:text-emerald-100 hover:bg-[#111C15] transition-colors"
            data-testid="qr-back-btn"
          >
            <ArrowLeft size={20} />
          </button>
          <div className="flex-1">
            <h1 className="font-heading text-2xl font-bold text-emerald-50 flex items-center gap-2">
              <QrCode size={20} className="text-emerald-400" /> QR-Code Labels
            </h1>
            <p className="text-emerald-100/50 text-sm">
              {selectedPlants.length} von {enrichedPlants.length} Pflanze(n) ausgewählt
            </p>
          </div>
          <button
            onClick={handlePrint}
            className="btn-primary flex items-center gap-2"
            data-testid="print-qr-btn"
          >
            <Printer size={16} /> Drucken
          </button>
        </div>

        {/* Project filter */}
        {projects.length > 0 && (
          <div className="flex items-center gap-3">
            <select
              className="input-grow w-auto text-sm"
              value={filterProject}
              onChange={e => { setFilterProject(e.target.value); setSelected(new Set(plants.filter(p => !e.target.value || p.project_id === e.target.value).map(p => p.id))); }}
              data-testid="qr-project-filter"
            >
              <option value="">Alle Projekte</option>
              {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            <span className="text-xs text-emerald-100/30">{enrichedPlants.length} Pflanzen</span>
          </div>
        )}

        {/* Plant selector */}
        {enrichedPlants.length === 0 ? (
          <div className="card-grow p-8 text-center">
            <p className="text-emerald-100/40 text-sm">Keine Pflanzen für dieses Projekt</p>
          </div>
        ) : (
          <div className="card-grow p-4">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-heading font-semibold text-emerald-100 text-sm">Pflanzen auswählen</h2>
              <div className="flex gap-2">
                <button onClick={selectAll} className="text-xs text-emerald-400 hover:text-emerald-300 transition-colors">
                  Alle
                </button>
                <span className="text-emerald-100/20">|</span>
                <button onClick={selectNone} className="text-xs text-emerald-100/40 hover:text-emerald-100 transition-colors">
                  Keine
                </button>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
              {enrichedPlants.map(p => (
                <label
                  key={p.id}
                  className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-colors ${
                    selected.has(p.id)
                      ? 'bg-emerald-500/15 border border-emerald-500/40'
                      : 'hover:bg-[#111C15]'
                  }`}
                  style={{ border: selected.has(p.id) ? undefined : '1px solid #1B2A21' }}
                >
                  <input
                    type="checkbox"
                    checked={selected.has(p.id)}
                    onChange={() => togglePlant(p.id)}
                    className="accent-emerald-500 flex-shrink-0"
                    data-testid={`select-qr-${p.id}`}
                  />
                  <div className="min-w-0">
                    <p className="text-sm text-emerald-100 truncate">{p.name}</p>
                    <p className="text-xs text-emerald-100/40 truncate">{PHASE_LABELS[p.phase] || p.phase}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>
        )}

        {/* Preview */}
        {selectedPlants.length > 0 && (
          <div>
            <h2 className="font-heading font-semibold text-emerald-100 mb-3 text-sm">
              Vorschau ({selectedPlants.length} Label{selectedPlants.length !== 1 ? 's' : ''})
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {selectedPlants.map(p => (
                <div key={p.id} className="p-3 rounded-xl" style={{ background: '#f8f9fa' }}>
                  <QRLabel plant={p} />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Print area – only visible on print */}
      <div className="print-grid" style={{ display: 'none' }}>
        {selectedPlants.map(p => (
          <QRLabel key={p.id} plant={p} />
        ))}
      </div>
    </div>
  );
}
